<template>
  <div class="no-target">
    <div class="v3-pass-comments-counter comments-column">
      <div class="ended-by">
        <div v-if="passEndedBy" class="mb-2">
          {{ passEndedBy }}
        </div>
      </div>
      <div v-if="pass.latest_comment">
        <button
          type="button"
          class="pass-comments-trigger d-flex align-items-center me-2 p-0 border-0 bg-transparent text-start"
          :aria-label="commentsButtonAriaLabel"
          :disabled="onlyLatestComment"
          @click="showModal()"
        >
          <div
            v-if="pass.latest_comment.comment && !isBasicView"
            class="comments-latest"
          >
            {{ pass.latest_comment.comment }}
          </div>
          <div v-if="!onlyLatestComment && !fromRetained" class="d-flex">
            <b class="comments-counter no-target me-2">{{
              pass.comments_count
            }}</b>
            <i class="ri-chat-new-line"></i>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"
import CommentsComponent from "@/v3components/CommentsComponent.vue"

export default {
  name: "PassComments",
  props: {
    pass: {
      type: Object,
      required: true
    },
    commentableType: {
      type: String,
      required: true
    },
    isBasicView: {
      type: Boolean,
      default: false
    },
    onlyLatestComment: {
      type: Boolean,
      default: false
    },
    fromRetained: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const activePass = computed(() => {
      return store.getters["dashboardTable/activeEditablePass"]
    })

    const passEndedBy = computed(() => {
      if (
        (props.pass.badge_flags && props.pass.badge_flags.system_completed) ||
        (props.pass.badge_flags &&
          props.pass.badge_flags.extended &&
          props.pass.badge_flags.ended)
      ) {
        return "Ended by System"
      }
      if (props.pass.child) {
        return props.pass.child.completed_by_user &&
          props.pass.child.completed_by_user.name
          ? "Ended by " + props.pass.child.completed_by_user.name
          : ""
      } else {
        return props.pass.completed_by_user
          ? "Ended by " + props.pass.completed_by_user.name
          : ""
      }
    })
    const studentName = computed(() => {
      return activePass.value && activePass.value.user
        ? activePass.value.user.first_name +
            " " +
            activePass.value.user.last_name
        : null
    })

    /** Row-scoped display name for this pass (not store `activePass`) — used in unique button labels. */
    const passRowContextName = computed(() => {
      const p = props?.pass
      if (!p) return ""
      const u = p.user
      if (u) {
        if (u.first_name || u.last_name) {
          return `${(u.first_name || "").trim()} ${(u.last_name || "").trim()}`.trim()
        }
        if (typeof u.name === "string" && u.name.trim()) {
          return u.name.trim()
        }
      }
      const cu = p.child?.user
      if (cu && (cu.first_name || cu.last_name)) {
        return `${(cu.first_name || "").trim()} ${(cu.last_name || "").trim()}`.trim()
      }
      const ch = p.child
      if (ch && (ch.first_name || ch.last_name)) {
        return `${(ch.first_name || "").trim()} ${(ch.last_name || "").trim()}`.trim()
      }
      return ""
    })

    const commentsButtonAriaLabel = computed(() => {
      const commentsCount = Number(props?.pass?.comments_count ?? 0)
      const countPhrase =
        commentsCount === 1
          ? t("common.a11y_pass_comments_count_one")
          : t("common.a11y_pass_comments_count_many", { count: commentsCount })
      const name = passRowContextName.value?.trim?.() || ""
      if (name) {
        return t("common.a11y_open_pass_comments_for_student", {
          name,
          countPhrase
        })
      }
      const passId = props?.pass?.id
      if (passId != null && passId !== "") {
        return t("common.a11y_open_pass_comments_for_pass_id", {
          id: passId,
          countPhrase
        })
      }
      return t("common.a11y_open_pass_comments_generic", { countPhrase })
    })

    const showModal = () => {
      if (!props.onlyLatestComment) {
        store.commit("dashboardTable/SET_ACTIVE_EDITABLE_PASS", props.pass)
        modal.open(CommentsComponent, {
          size: "lg",
          title: studentName,
          props: {
            toID: activePass.value ? activePass.value.id : null,
            commentableType: props.commentableType,
            silent: false,
            disableComment: props.fromRetained
          }
        })
      }
    }

    return { passEndedBy, showModal, commentsButtonAriaLabel }
  }
}
</script>

<style lang="scss">
.pass-comments-trigger {
  color: inherit;
  font: inherit;
  line-height: inherit;
}

.pass-comments-trigger:disabled {
  opacity: 1;
}

.vue-recycle-scroller__item-wrapper {
  .vue-recycle-scroller__item-view[comments-modal] {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 999999999999999 !important;
    z-index: 999999999999999 !important;
    transform: none !important;
    & .t-row {
      border-color: transparent;
    }
    & .t-row > div {
      visibility: hidden;
    }
    & .t-row > div.no-target {
      visibility: visible;
    }
    .comments-column {
      visibility: hidden !important;
    }
    .modal {
      overflow: visible !important;
    }
  }
}
</style>
